import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "../styles/Navbar.css";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import cccLogo from "../assets/ccc-logo.png";

export default function Navbar() {
  const { user, logout, profile } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      nav("/");
    } catch (err) {
      console.error("Logout failed:", err.message);
      alert("Failed to logout. Please try again.");
    }
  };

  const navItems = [
    { to: "/", label: "Home", end: true },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/book", label: "Book" },
  ];

  return (
    <header className="site-nav">
      <div className="container nav-inner">
        {/* Logo */}
        <motion.div
          className="brand"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <button
            className="logo-link"
            onClick={() => setShowLogoModal(true)}
            aria-label="Open logo"
          >
            <img className="logo-img" src={cccLogo} alt="Chennai Car Care logo" />
            <span className="logo-text">CHENNAI CAR CARE</span>
          </button>
        </motion.div>

        {/* Desktop Nav */}
        <nav className="links desktop">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => (isActive ? "active-link" : undefined)}
            >
              {n.label}
            </NavLink>
          ))}

          {user && (
            <NavLink
              to="/online-bookings"
              className={({ isActive }) =>
                isActive ? "active-link" : undefined
              }
            >
              OnlineBookings
            </NavLink>
          )}

          {profile?.role === "admin" && (
            <NavLink
              to="/admin-dashboard"
              className={({ isActive }) =>
                isActive ? "active-link" : undefined
              }
            >
              Admin
            </NavLink>
          )}

          {!user ? (
            <>
              <NavLink to="/login">Login</NavLink>
              <NavLink to="/register">Register</NavLink>
            </>
          ) : (
            <>
              <NavLink to="/profile" className="primary">
                {profile?.name ? profile.name : "Profile"}
              </NavLink>
              <button className="link-btn" onClick={handleLogout}>
                Logout
              </button>
            </>
          )}
        </nav>

        {/* Mobile Nav */}
        <div className="mobile">
          <motion.button
            className={`hamburger ${open ? "open" : ""}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            whileTap={{ scale: 0.85 }}
          >
            <span></span>
            <span></span>
            <span></span>
          </motion.button>

          <AnimatePresence>
            {open && (
              <motion.div
                className="mobile-menu"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.25 }}
              >
                {navItems.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      isActive ? "active-link" : undefined
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}

                {user && (
                  <NavLink
                    to="/online-bookings"
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      isActive ? "active-link" : undefined
                    }
                  >
                    OnlineBookings
                  </NavLink>
                )}

                {profile?.role === "admin" && (
                  <NavLink to="/admin-dashboard" onClick={() => setOpen(false)}>
                    Admin
                  </NavLink>
                )}

                {!user ? (
                  <>
                    <NavLink to="/login" onClick={() => setOpen(false)}>
                      Login
                    </NavLink>
                    <NavLink to="/register" onClick={() => setOpen(false)}>
                      Register
                    </NavLink>
                  </>
                ) : (
                  <>
                    <NavLink
                      to="/profile"
                      className="primary"
                      onClick={() => setOpen(false)}
                    >
                      {profile?.name ? profile.name : "Profile"}
                    </NavLink>
                    <button
                      className="link-btn"
                      onClick={() => {
                        setOpen(false);
                        handleLogout();
                      }}
                    >
                      Logout
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Logo Modal */}
      <AnimatePresence>
        {showLogoModal && (
          <motion.div
            className="logo-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="logo-modal"
              initial={{ scale: 0.95, opacity: 0, y: -40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 40 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <img src={cccLogo} alt="Chennai Car Care full logo" />
              <button
                className="close-btn"
                onClick={() => setShowLogoModal(false)}
                aria-label="Close logo"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
